// @ts-nocheck
import type { ViewState } from './CanvasRenderer';
import { getDefaultFontStack } from '../tableau/fontRegistry';

const DEFAULT_RENDERER_FONT_FAMILY = getDefaultFontStack('ui')

const getMergeAnchorOffsets = (format: {
    mergeAnchorRowOffset?: number,
    mergeAnchorColOffset?: number,
} | undefined): { row: number, col: number } => ({
    row: Math.max(0, Math.floor(Number(format?.mergeAnchorRowOffset) || 0)),
    col: Math.max(0, Math.floor(Number(format?.mergeAnchorColOffset) || 0)),
});

const isMergedChildCell = (format: {
    mergeAnchorRowOffset?: number,
    mergeAnchorColOffset?: number,
} | undefined): boolean => {
    const offsets = getMergeAnchorOffsets(format);
    return offsets.row > 0 || offsets.col > 0;
};

const getMergedSpanSize = (
    startIndex: number,
    spanRaw: number | undefined,
    count: number,
    measure: (index: number) => number,
): number => {
    const span = Math.max(1, Math.floor(Number(spanRaw) || 1));
    let total = 0;
    for (let i = 0; i < span && (startIndex + i) < count; i++) {
        total += measure(startIndex + i);
    }
    return total;
};

const drawTextWithDecoration = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    underline: boolean | string | undefined,
    strikethrough: boolean | undefined,
) => {
    ctx.fillText(text, x, y);
    if (!underline && !strikethrough) return;

    const width = ctx.measureText(text).width;
    let startX = x;
    if (ctx.textAlign === 'center') {
        startX = x - width / 2;
    } else if (ctx.textAlign === 'right' || ctx.textAlign === 'end') {
        startX = x - width;
    }

    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = ctx.fillStyle;
    if (underline) {
        const underlineY = Math.floor(y + 2);
        ctx.beginPath();
        ctx.moveTo(Math.floor(startX), underlineY);
        ctx.lineTo(Math.ceil(startX + width), underlineY);
        ctx.stroke();
        if (typeof underline === 'string' && underline.toLowerCase().includes('double')) {
            const secondUnderlineY = underlineY + 2;
            ctx.beginPath();
            ctx.moveTo(Math.floor(startX), secondUnderlineY);
            ctx.lineTo(Math.ceil(startX + width), secondUnderlineY);
            ctx.stroke();
        }
    }
    if (strikethrough) {
        const strikeY = Math.floor(y - 3);
        ctx.beginPath();
        ctx.moveTo(Math.floor(startX), strikeY);
        ctx.lineTo(Math.ceil(startX + width), strikeY);
        ctx.stroke();
    }
    ctx.restore();
};

const styleSection = (format: any, key: string): any => (
    format?.[key] && typeof format[key] === 'object' && !Array.isArray(format[key])
        ? format[key]
        : {}
);

const hidesConditionalFormatText = (format: any): boolean => {
    const dataBar = styleSection(format, 'dataBar');
    if (dataBar.showValue === false) return true;
    const iconSet = styleSection(format, 'iconSet');
    return iconSet.showValue === false;
};

const looksNumericText = (s: string): boolean => {
    const t = String(s ?? '').trim();
    if (!t) return false;
    // Accept common formatted numerics: -123, 1,234.56, (123), $1,234.00, 12.3K, 4.5M
    const cleaned = t
        .replace(/^[\(\s]+/, '')
        .replace(/[\)\s]+$/, '')
        .replace(/^[\$€£¥]/, '')
        .replace(/%$/, '')
        .replace(/,/g, '')
        .replace(/[KMB]$/i, '');
    if (!cleaned) return false;
    return /^-?\d+(\.\d+)?$/.test(cleaned);
};

const numberFormatOverflowsAsHashes = (format: any, text: string): boolean => {
    const numberFormat = styleSection(format, 'numberFormat');
    const category = String(format?.numberFormatCategory ?? numberFormat.category ?? '').toLowerCase();
    if (category === 'text') return false;
    if ([
        'number',
        'currency',
        'accounting',
        'date',
        'time',
        'percentage',
        'fraction',
        'scientific',
        'special',
        'custom',
    ].includes(category)) return true;
    return looksNumericText(text);
};

const resolvedTextStyle = (format: any) => {
    const font = styleSection(format, 'font');
    const alignment = styleSection(format, 'alignment');
    const rawTextRotation = format?.textRotation ?? alignment.textRotation;
    return {
        bold: Boolean(format?.bold ?? font.bold),
        italic: Boolean(format?.italic ?? font.italic),
        underline: format?.underline ?? font.underline,
        strikethrough: Boolean(format?.strikethrough ?? font.strikethrough),
        align: normalizeHorizontalAlign(format?.align ?? alignment.horizontal),
        verticalAlign: normalizeVerticalAlign(format?.verticalAlign ?? alignment.vertical),
        wrapText: Boolean(format?.wrapText ?? alignment.wrapText),
        shrinkToFit: Boolean(format?.shrinkToFit ?? alignment.shrinkToFit),
        indent: Math.max(0, Number(format?.indent ?? alignment.indent) || 0),
        textRotation: Number(rawTextRotation) || 0,
        verticalText: String(rawTextRotation ?? '').trim().toLowerCase() === 'vertical' || Number(rawTextRotation) === 255,
        fontSize: Number(format?.fontSize ?? font.size) || 13,
        fontFamily: String(format?.fontFamily ?? font.family ?? DEFAULT_RENDERER_FONT_FAMILY),
        textColor: String(format?.textColor ?? font.color ?? '#000000'),
        script: String(font.script ?? 'normal'),
        readingOrder: String(alignment.readingOrder ?? 'context'),
    };
};

const getTextPadding = (style: ReturnType<typeof resolvedTextStyle>) => 6 + style.indent * 12;

const normalizeHorizontalAlign = (align: unknown): string | undefined => {
    const value = String(align ?? '').trim();
    if (!value) return undefined;
    if (['centerAcrossSelection', 'centerAcross', 'centerContinuous'].includes(value)) return 'centerContinuous';
    return value;
};

const normalizeVerticalAlign = (align: unknown): string | undefined => {
    const value = String(align ?? '').trim();
    if (value === 'middle') return 'center';
    return value || undefined;
};

const defaultHorizontalAlign = (style: ReturnType<typeof resolvedTextStyle>, text: string, isMerged: boolean): string => {
    const align = normalizeHorizontalAlign(style.align);
    if (align && align !== 'general') return align;
    if (isMerged) return 'center';
    return looksNumericText(text) ? 'right' : 'left';
};

const buildCanvasFont = (style: ReturnType<typeof resolvedTextStyle>, fontSize = style.fontSize): string => {
    let font = `${fontSize}px ${style.fontFamily}`;
    if (style.bold) font = 'bold ' + font;
    if (style.italic) font = 'italic ' + font;
    return font;
};

const setCanvasFont = (ctx: CanvasRenderingContext2D, style: ReturnType<typeof resolvedTextStyle>, fontSize = style.fontSize) => {
    ctx.font = buildCanvasFont(style, fontSize);
};

const repeatTextForFillAlignment = (
    ctx: CanvasRenderingContext2D,
    text: string,
    usableWidth: number,
): string => {
    if (!text) return text;
    const width = Math.max(1, ctx.measureText(text).width || 1);
    const repeats = Math.max(1, Math.ceil(usableWidth / width) + 1);
    let filled = text.repeat(repeats);
    while (filled.length > text.length && ctx.measureText(filled).width > usableWidth + width) {
        filled = filled.slice(0, -text.length);
    }
    return filled;
};

const hashOverflowText = (
    ctx: CanvasRenderingContext2D,
    usableWidth: number,
): string => {
    const hashWidth = Math.max(1, ctx.measureText('#').width || 7);
    return '#'.repeat(Math.max(1, Math.floor(usableWidth / hashWidth)));
};

const wrapTextToLines = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
): string[] => {
    const hardLines = String(text ?? '').split(/\r\n|\n|\r/);
    const lines: string[] = [];
    for (const hardLine of hardLines) {
        const words = hardLine.split(/(\s+)/).filter((part) => part.length > 0);
        let current = '';
        for (const word of words) {
            const candidate = current ? `${current}${word}` : word;
            if (ctx.measureText(candidate).width <= maxWidth || !current) {
                current = candidate;
            } else {
                lines.push(current.trimEnd());
                current = word.trimStart();
            }
        }
        lines.push(current || '');
    }
    return lines.length > 0 ? lines : [''];
};

const splitHardLines = (text: string): string[] => {
    const lines = String(text ?? '').split(/\r\n|\n|\r/);
    return lines.length > 0 ? lines : [''];
};

const getTextLayoutLines = (
    ctx: CanvasRenderingContext2D,
    text: string,
    style: ReturnType<typeof resolvedTextStyle>,
    usableWidth: number,
): string[] => (
    style.wrapText
        ? wrapTextToLines(ctx, text, usableWidth)
        : splitHardLines(text)
);

const getScriptAdjustedFontSize = (style: ReturnType<typeof resolvedTextStyle>): number => {
    const fontSize = Math.max(1, Number(style.fontSize) || 13);
    return (style.script === 'superscript' || style.script === 'subscript')
        ? Math.max(7, Math.floor(fontSize * 0.72))
        : fontSize;
};

const getLineHeight = (fontSize: number): number => Math.max(10, Math.ceil(fontSize * 1.25));

const maxMeasuredLineWidth = (
    ctx: CanvasRenderingContext2D,
    lines: string[],
): number => lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width || 0), 0);

const drawJustifiedLine = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    width: number,
    underline: boolean | string | undefined,
    strikethrough: boolean | undefined,
): boolean => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return false;
    const wordsWidth = words.reduce((sum, word) => sum + ctx.measureText(word).width, 0);
    const gap = (width - wordsWidth) / (words.length - 1);
    if (!Number.isFinite(gap) || gap <= 0) return false;
    ctx.textAlign = 'left';
    let cursor = x;
    for (const word of words) {
        drawTextWithDecoration(ctx, word, cursor, y, underline, strikethrough);
        cursor += ctx.measureText(word).width + gap;
    }
    return true;
};

const drawDistributedLine = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    width: number,
    underline: boolean | string | undefined,
    strikethrough: boolean | undefined,
): boolean => {
    const glyphs = Array.from(text);
    if (glyphs.length < 2) return false;
    const glyphWidth = glyphs.reduce((sum, glyph) => sum + ctx.measureText(glyph).width, 0);
    const gap = (width - glyphWidth) / (glyphs.length - 1);
    if (!Number.isFinite(gap) || gap <= 0) return false;
    ctx.textAlign = 'left';
    let cursor = x;
    for (const glyph of glyphs) {
        drawTextWithDecoration(ctx, glyph, cursor, y, underline, strikethrough);
        cursor += ctx.measureText(glyph).width + gap;
    }
    return true;
};

const drawTextBlock = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    style: ReturnType<typeof resolvedTextStyle>,
) => {
    const padding = getTextPadding(style);
    const usableWidth = Math.max(4, width - padding * 2);
    let fontSize = style.fontSize;
    const scriptOffset = style.script === 'superscript'
        ? -Math.max(3, Math.floor(fontSize * 0.32))
        : style.script === 'subscript'
            ? Math.max(3, Math.floor(fontSize * 0.22))
            : 0;
    fontSize = getScriptAdjustedFontSize(style);
    setCanvasFont(ctx, style, fontSize);
    if (style.shrinkToFit && !style.wrapText) {
        const measured = ctx.measureText(text).width;
        if (measured > usableWidth) {
            fontSize = Math.max(7, Math.floor(fontSize * usableWidth / Math.max(1, measured)));
            setCanvasFont(ctx, style, fontSize);
        }
    }
    if (style.align === 'fill' && !style.wrapText && !style.textRotation && !style.verticalText) {
        text = repeatTextForFillAlignment(ctx, text, usableWidth);
    }
    ctx.direction = style.readingOrder === 'rightToLeft'
        ? 'rtl'
        : style.readingOrder === 'leftToRight'
            ? 'ltr'
            : 'inherit';
    const lineHeight = getLineHeight(fontSize);
    const lines = getTextLayoutLines(ctx, text, style, usableWidth);
    const totalHeight = lineHeight * lines.length;
    let textY = Math.floor(y + height / 2);
    let lineAdvance = lineHeight;
    if ((style.verticalAlign === 'justify' || style.verticalAlign === 'distributed') && lines.length > 1) {
        const remaining = Math.max(0, height - totalHeight);
        const gap = style.verticalAlign === 'distributed'
            ? remaining / (lines.length + 1)
            : Math.max(0, remaining - 4) / (lines.length - 1);
        textY = style.verticalAlign === 'distributed'
            ? Math.floor(y + gap + lineHeight / 2)
            : Math.floor(y + lineHeight / 2 + 2);
        lineAdvance = lineHeight + gap;
    } else if (style.verticalAlign === 'top') textY = Math.floor(y + lineHeight / 2 + 2);
    else if (style.verticalAlign === 'bottom') textY = Math.floor(y + height - totalHeight + lineHeight / 2 - 2);
    else textY = Math.floor(y + height / 2 - totalHeight / 2 + lineHeight / 2);
    if (style.verticalText && !style.wrapText) {
        const chars = Array.from(text);
        const totalVerticalHeight = chars.length * lineHeight;
        let charY = style.verticalAlign === 'top'
            ? Math.floor(y + lineHeight / 2 + 2)
            : style.verticalAlign === 'bottom'
                ? Math.floor(y + height - totalVerticalHeight + lineHeight / 2 - 2)
                : Math.floor(y + height / 2 - totalVerticalHeight / 2 + lineHeight / 2);
        const textX = style.align === 'right'
            ? Math.floor(x + width) - padding
            : style.align === 'left'
                ? Math.floor(x) + padding
                : Math.floor(x + width / 2);
        ctx.textAlign = style.align === 'right' ? 'right' : style.align === 'left' ? 'left' : 'center';
        for (const char of chars) {
            drawTextWithDecoration(ctx, char, textX, charY + scriptOffset, style.underline, style.strikethrough);
            charY += lineHeight;
        }
        return;
    }
    if (style.textRotation && !style.wrapText) {
        const angle = Math.max(-90, Math.min(90, Number(style.textRotation) || 0));
        const textX = style.align === 'center'
            ? Math.floor(x + width / 2)
            : style.align === 'right'
                ? Math.floor(x + width) - padding
                : Math.floor(x) + padding;
        ctx.save();
        ctx.translate(textX, Math.floor(y + height / 2));
        ctx.rotate(-angle * Math.PI / 180);
        ctx.textAlign = style.align === 'center' ? 'center' : style.align === 'right' ? 'right' : 'left';
        drawTextWithDecoration(ctx, text, 0, 0, style.underline, style.strikethrough);
        ctx.restore();
        return;
    }
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        let textX = Math.floor(x) + padding;
        ctx.textAlign = 'left';
        if (style.align === 'distributed') {
            if (drawDistributedLine(ctx, line, Math.floor(x) + padding, textY + scriptOffset, usableWidth, style.underline, style.strikethrough)) {
                textY += lineAdvance;
                continue;
            }
        } else if (style.align === 'justify' && lineIndex < lines.length - 1) {
            if (drawJustifiedLine(ctx, line, Math.floor(x) + padding, textY + scriptOffset, usableWidth, style.underline, style.strikethrough)) {
                textY += lineAdvance;
                continue;
            }
        } else if (style.align === 'center' || style.align === 'centerContinuous') {
            textX = Math.floor(x + width / 2);
            ctx.textAlign = 'center';
        } else if (style.align === 'right') {
            textX = Math.floor(x + width) - padding;
            ctx.textAlign = 'right';
        }
        drawTextWithDecoration(ctx, line, textX, textY + scriptOffset, style.underline, style.strikethrough);
        textY += lineAdvance;
    }
};

export class TextRenderer {
    private ctx: CanvasRenderingContext2D;

    constructor(ctx: CanvasRenderingContext2D) {
        this.ctx = ctx;
    }

    render(
        viewState: ViewState,
        getColumnWidth: (col: number) => number,
        getRowHeight: (row: number) => number,
        getCellValue: (col: number, row: number) => string | undefined,
        getCellFormat: (col: number, row: number) => {
            bold?: boolean,
            italic?: boolean,
            underline?: boolean | string,
            strikethrough?: boolean,
            align?: string,
            verticalAlign?: string,
            wrapText?: boolean,
            shrinkToFit?: boolean,
            indent?: number,
            textRotation?: number,
            fontSize?: number,
            fontFamily?: string,
            textColor?: string,
            font?: Record<string, unknown>,
            alignment?: Record<string, unknown>,
        } | undefined,
        colCount: number,
        rowCount: number
    ) {
        const { scrollX, scrollY, viewportWidth, viewportHeight } = viewState;
        const frozenRowsTop = Math.max(0, Math.floor(viewState.frozenRowsTop ?? 0));
        const frozenColsLeft = Math.max(0, Math.floor(viewState.frozenColsLeft ?? 0));
        const ctx = this.ctx;

        ctx.save();
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#000000';

        // Compute frozen header pixel height.
        let frozenHeight = 0;
        if (frozenRowsTop > 0) {
            for (let r = 0; r < Math.min(frozenRowsTop, rowCount); r++) {
                frozenHeight += getRowHeight(r);
            }
        }
        let frozenWidth = 0;
        if (frozenColsLeft > 0) {
            for (let c = 0; c < Math.min(frozenColsLeft, colCount); c++) {
                frozenWidth += getColumnWidth(c);
            }
        }

        // Fast path: render only visible window when controller provides bounds.
        const startCol = viewState.startCol ?? 0;
        const endCol = viewState.endCol ?? (colCount - 1);
        const startRow = viewState.startRow ?? 0;
        const endRow = viewState.endRow ?? (rowCount - 1);
        const baseX = viewState.startX ?? -scrollX;
        const baseY = viewState.startY ?? -scrollY;

        const resolveTextPaint = (
            col: number,
            row: number,
            text: string,
            cellX: number,
            cellWidth: number,
            mergedWidth: number,
            mergedHeight: number,
            format: any,
            style: ReturnType<typeof resolvedTextStyle>,
        ) => {
            const result = {
                text,
                clipX: cellX,
                clipWidth: mergedWidth,
                clipHeight: mergedHeight,
                drawX: cellX,
                drawWidth: mergedWidth,
            };
            const singleCell = Math.max(1, Math.floor(Number(format?.mergeColSpan) || 1)) <= 1
                && Math.max(1, Math.floor(Number(format?.mergeRowSpan) || 1)) <= 1;
            if (singleCell && style.align === 'centerContinuous' && !style.wrapText && !style.textRotation && !style.verticalText) {
                let spanWidth = cellWidth;
                for (let nextCol = col + 1; nextCol < colCount; nextCol += 1) {
                    const adjacentText = getCellValue(nextCol, row);
                    const adjacentFormat = getCellFormat(nextCol, row);
                    if (adjacentText || isMergedChildCell(adjacentFormat)) break;
                    spanWidth += getColumnWidth(nextCol);
                }
                result.clipWidth = Math.max(mergedWidth, spanWidth);
                result.drawWidth = Math.max(mergedWidth, spanWidth);
                return result;
            }
            if (!singleCell || style.wrapText || style.shrinkToFit || style.textRotation || style.verticalText) return result;

            setCanvasFont(ctx, style);
            const usableWidth = Math.max(4, cellWidth - getTextPadding(style) * 2);
            const measured = ctx.measureText(text).width;
            if (measured <= usableWidth) return result;

            if (numberFormatOverflowsAsHashes(format, text)) {
                result.text = hashOverflowText(ctx, usableWidth);
                return result;
            }

            if ((style.align || 'left') !== 'left') return result;
            let spillWidth = cellWidth;
            for (let nextCol = col + 1; nextCol < colCount; nextCol += 1) {
                const adjacentText = getCellValue(nextCol, row);
                const adjacentFormat = getCellFormat(nextCol, row);
                if (adjacentText || isMergedChildCell(adjacentFormat)) break;
                spillWidth += getColumnWidth(nextCol);
                if (spillWidth - getTextPadding(style) * 2 >= measured) break;
            }
            result.clipWidth = Math.max(mergedWidth, spillWidth);
            return result;
        };

        // Clip scrolling text so it never draws under the frozen header area.
        ctx.save();
        if ((frozenRowsTop > 0 && frozenHeight > 0) || (frozenColsLeft > 0 && frozenWidth > 0)) {
            ctx.beginPath();
            const clipX = Math.min(frozenWidth, viewportWidth);
            const clipY = Math.min(frozenHeight, viewportHeight);
            ctx.rect(clipX, clipY, Math.max(0, viewportWidth - clipX), Math.max(0, viewportHeight - clipY));
            ctx.clip();
        }

        let currentX = baseX;
        let bodyStartCol = startCol;
        if (frozenColsLeft > 0 && bodyStartCol < frozenColsLeft) {
            for (let col = bodyStartCol; col < frozenColsLeft && col < colCount; col++) {
                currentX += getColumnWidth(col);
            }
            bodyStartCol = frozenColsLeft;
        }
        for (let col = bodyStartCol; col <= endCol && col < colCount; col++) {
            if (currentX >= viewportWidth) break;
            const w = getColumnWidth(col);
            if (w <= 0) {
                continue;
            }

            // Scrolling pass: skip frozen rows; we'll paint them pinned afterwards.
            let currentY = baseY;
            let r0 = startRow;
            if (frozenRowsTop > 0 && r0 < frozenRowsTop) {
                for (let r = r0; r < frozenRowsTop && r < rowCount; r++) {
                    currentY += getRowHeight(r);
                }
                r0 = frozenRowsTop;
            }

            for (let row = r0; row <= endRow && row < rowCount; row++) {
                if (currentY >= viewportHeight) break;
                const h = getRowHeight(row);
                if (h <= 0) {
                    continue;
                }

                if (currentX + w > 0 && currentY + h > 0) {
                    const format = getCellFormat(col, row);
                    if (isMergedChildCell(format)) {
                        currentY += h;
                        continue;
                    }
                    const text = hidesConditionalFormatText(format) ? undefined : getCellValue(col, row);
                    if (text) {
                        const mergedWidth = getMergedSpanSize(col, format?.mergeColSpan, colCount, getColumnWidth);
                        const mergedHeight = getMergedSpanSize(row, format?.mergeRowSpan, rowCount, getRowHeight);
                        const style = resolvedTextStyle(format);
                        style.align = defaultHorizontalAlign(style, text, (Number(format?.mergeColSpan) || 1) > 1 || (Number(format?.mergeRowSpan) || 1) > 1);
                        const paint = resolveTextPaint(col, row, text, currentX, w, mergedWidth, mergedHeight, format, style);

                        // Clip to cell area
                        ctx.save();
                        ctx.beginPath();
                        ctx.rect(Math.floor(paint.clipX), Math.floor(currentY), Math.floor(paint.clipWidth), Math.floor(paint.clipHeight));
                        ctx.clip();

                        ctx.fillStyle = style.textColor;
                        drawTextBlock(ctx, paint.text, paint.drawX, currentY, paint.drawWidth, mergedHeight, style);
                        ctx.restore();
                    }
                }

                currentY += h;
            }

            currentX += w;
        }

        // End scrolling-text clip.
        ctx.restore();

        // Frozen rows (pinned at top): draw on top so they remain visible.
        if (frozenRowsTop > 0) {
            ctx.save();
            if (frozenColsLeft > 0 && frozenWidth > 0) {
                ctx.beginPath();
                const clipX = Math.min(frozenWidth, viewportWidth);
                ctx.rect(clipX, 0, Math.max(0, viewportWidth - clipX), Math.min(frozenHeight, viewportHeight));
                ctx.clip();
            }
            let fx = baseX;
            for (let col = startCol; col <= endCol && col < colCount; col++) {
                if (fx >= viewportWidth) break;
                const w = getColumnWidth(col);
                if (w <= 0) continue;

                let fy = 0;
                for (let row = 0; row < frozenRowsTop && row < rowCount; row++) {
                    const h = getRowHeight(row);
                    if (h <= 0) continue;

                    if (fx + w > 0 && fy + h > 0) {
                        const format = getCellFormat(col, row);
                        if (isMergedChildCell(format)) {
                            fy += h;
                            continue;
                        }
                        const text = hidesConditionalFormatText(format) ? undefined : getCellValue(col, row);
                        if (text) {
                            const mergedWidth = getMergedSpanSize(col, format?.mergeColSpan, colCount, getColumnWidth);
                            const mergedHeight = getMergedSpanSize(row, format?.mergeRowSpan, rowCount, getRowHeight);
                            const style = resolvedTextStyle(format);
                            style.align = defaultHorizontalAlign(style, text, (Number(format?.mergeColSpan) || 1) > 1 || (Number(format?.mergeRowSpan) || 1) > 1);
                            const paint = resolveTextPaint(col, row, text, fx, w, mergedWidth, mergedHeight, format, style);

                            // Clip to cell area
                            ctx.save();
                            ctx.beginPath();
                            ctx.rect(Math.floor(paint.clipX), Math.floor(fy), Math.floor(paint.clipWidth), Math.floor(paint.clipHeight));
                            ctx.clip();

                            ctx.fillStyle = style.textColor;
                            drawTextBlock(ctx, paint.text, paint.drawX, fy, paint.drawWidth, mergedHeight, style);
                            ctx.restore();
                        }
                    }

                    fy += h;
                }

                fx += w;
            }
            ctx.restore();
        }

        // Frozen left columns: draw pinned text after scrolled panes so the left edge remains readable.
        if (frozenColsLeft > 0) {
            let pinnedX = 0;
            for (let col = 0; col < frozenColsLeft && col < colCount; col++) {
                if (pinnedX >= viewportWidth) break;
                const w = getColumnWidth(col);
                if (w <= 0) continue;

                const drawCellText = (row: number, cellX: number, cellY: number, h: number) => {
                    if (cellX + w <= 0 || cellY + h <= 0 || cellY >= viewportHeight) return;
                    const format = getCellFormat(col, row);
                    if (isMergedChildCell(format)) return;
                    const text = hidesConditionalFormatText(format) ? undefined : getCellValue(col, row);
                    if (!text) return;

                    const mergedWidth = getMergedSpanSize(col, format?.mergeColSpan, colCount, getColumnWidth);
                    const mergedHeight = getMergedSpanSize(row, format?.mergeRowSpan, rowCount, getRowHeight);
                    const style = resolvedTextStyle(format);
                    style.align = defaultHorizontalAlign(style, text, (Number(format?.mergeColSpan) || 1) > 1 || (Number(format?.mergeRowSpan) || 1) > 1);
                    const paint = resolveTextPaint(col, row, text, cellX, w, mergedWidth, mergedHeight, format, style);

                    ctx.save();
                    ctx.beginPath();
                    ctx.rect(Math.floor(paint.clipX), Math.floor(cellY), Math.floor(paint.clipWidth), Math.floor(paint.clipHeight));
                    ctx.clip();

                    ctx.fillStyle = style.textColor;
                    drawTextBlock(ctx, paint.text, paint.drawX, cellY, paint.drawWidth, mergedHeight, style);
                    ctx.restore();
                };

                let pinnedY = 0;
                for (let row = 0; row < frozenRowsTop && row < rowCount; row++) {
                    const h = getRowHeight(row);
                    if (h > 0) drawCellText(row, pinnedX, pinnedY, h);
                    pinnedY += h;
                }

                ctx.save();
                if (frozenHeight > 0) {
                    ctx.beginPath();
                    const clipY = Math.min(frozenHeight, viewportHeight);
                    ctx.rect(0, clipY, Math.min(frozenWidth, viewportWidth), Math.max(0, viewportHeight - clipY));
                    ctx.clip();
                }
                {
                    let bodyY = baseY;
                    let bodyStartRow = startRow;
                    if (frozenRowsTop > 0 && bodyStartRow < frozenRowsTop) {
                        for (let row = bodyStartRow; row < frozenRowsTop && row < rowCount; row++) {
                            bodyY += getRowHeight(row);
                        }
                        bodyStartRow = frozenRowsTop;
                    }
                    for (let row = bodyStartRow; row <= endRow && row < rowCount; row++) {
                        if (bodyY >= viewportHeight) break;
                        const h = getRowHeight(row);
                        if (h > 0) drawCellText(row, pinnedX, bodyY, h);
                        bodyY += h;
                    }
                }
                ctx.restore();

                pinnedX += w;
            }
        }

        ctx.restore();
    }

    measureText(text: string, format?: {
        bold?: boolean,
        italic?: boolean,
        fontSize?: number,
        fontFamily?: string,
        font?: Record<string, unknown>,
    }): number {
        this.ctx.save();
        const style = resolvedTextStyle(format);
        const fontSize = Number(style.fontSize || 0) > 0 ? Number(style.fontSize) : 13;
        const fontFamily = String(style.fontFamily || DEFAULT_RENDERER_FONT_FAMILY);
        let font = `${fontSize}px ${fontFamily}`;
        if (style.bold) font = 'bold ' + font;
        if (style.italic) font = 'italic ' + font;
        this.ctx.font = font;
        const metrics = this.ctx.measureText(text);
        this.ctx.restore();
        return metrics.width;
    }

    measureCellText(text: string, format?: {
        bold?: boolean,
        italic?: boolean,
        underline?: boolean | string,
        strikethrough?: boolean,
        align?: string,
        verticalAlign?: string,
        wrapText?: boolean,
        shrinkToFit?: boolean,
        indent?: number,
        textRotation?: number,
        fontSize?: number,
        fontFamily?: string,
        textColor?: string,
        font?: Record<string, unknown>,
        alignment?: Record<string, unknown>,
    }, cellWidth?: number): {
        width: number,
        height: number,
        lineCount: number,
        fontSize: number,
        lineHeight: number,
        verticalText: boolean,
        rotated: boolean,
    } {
        this.ctx.save();
        const style = resolvedTextStyle(format);
        const fontSize = getScriptAdjustedFontSize(style);
        setCanvasFont(this.ctx, style, fontSize);

        const padding = getTextPadding(style);
        const textValue = String(text ?? '');
        const lineHeight = getLineHeight(fontSize);
        const usableWidth = Math.max(4, Number.isFinite(Number(cellWidth))
            ? Number(cellWidth) - padding * 2
            : Number.MAX_SAFE_INTEGER);

        if (style.verticalText && !style.wrapText) {
            const glyphs = Array.from(textValue.replace(/\r\n|\n|\r/g, ''));
            const glyphWidth = glyphs.length > 0
                ? glyphs.reduce((max, glyph) => Math.max(max, this.ctx.measureText(glyph).width || 0), 0)
                : 0;
            this.ctx.restore();
            return {
                width: Math.ceil(glyphWidth + padding * 2),
                height: Math.ceil(lineHeight * Math.max(1, glyphs.length) + 8),
                lineCount: Math.max(1, glyphs.length),
                fontSize,
                lineHeight,
                verticalText: true,
                rotated: false,
            };
        }

        if (style.textRotation && !style.wrapText) {
            const lines = splitHardLines(textValue);
            const textWidth = maxMeasuredLineWidth(this.ctx, lines);
            const textHeight = lineHeight * Math.max(1, lines.length);
            const angle = Math.abs(Math.max(-90, Math.min(90, Number(style.textRotation) || 0))) * Math.PI / 180;
            const rotatedWidth = Math.abs(textWidth * Math.cos(angle)) + Math.abs(textHeight * Math.sin(angle));
            const rotatedHeight = Math.abs(textWidth * Math.sin(angle)) + Math.abs(textHeight * Math.cos(angle));
            this.ctx.restore();
            return {
                width: Math.ceil(rotatedWidth + padding * 2),
                height: Math.ceil(rotatedHeight + 8),
                lineCount: Math.max(1, lines.length),
                fontSize,
                lineHeight,
                verticalText: false,
                rotated: true,
            };
        }

        const lines = getTextLayoutLines(this.ctx, textValue, style, usableWidth);
        const measuredWidth = maxMeasuredLineWidth(this.ctx, lines);
        this.ctx.restore();
        return {
            width: Math.ceil(measuredWidth + padding * 2),
            height: Math.ceil(lineHeight * Math.max(1, lines.length) + 8),
            lineCount: Math.max(1, lines.length),
            fontSize,
            lineHeight,
            verticalText: false,
            rotated: false,
        };
    }
}
